/**
 * Incidents client — mirrors `apps/api/src/domains/incidents/routes.ts`
 * verbatim.
 *
 * Six routes, a complete report → review → follow-up → resolve → close
 * lifecycle, and until now the string `incidents` did not appear anywhere in
 * either app. The domain was reachable only by curl.
 *
 * AN INCIDENT IS A FORM RESPONSE WITH A CASE FILE AROUND IT. Reporting one
 * writes a `form_response` against a PUBLISHED incident form version and an
 * `incident` row bound to it — `incident_content_fk` makes that binding
 * structural, so there is no incident whose narrative is not a versioned,
 * approved instrument. {@link IncidentDetail} hands back both halves.
 *
 * FOUR THINGS A SCREEN MUST NOT GET WRONG.
 *
 * 1. WITHHELD AND ABSENT ARE THE SAME ANSWER HERE, EVERYWHERE. On the list, an
 *    unreachable incident is simply not in the rows and there is NO total, so a
 *    count is a claim about the caller's reach and nothing wider. On the detail
 *    and every write, both cases are 404 with the same constant message. That is
 *    deliberate anti-enumeration, and it means a `NotFoundError` is never
 *    evidence the incident does not exist.
 * 2. AN INCIDENT WITH NO CLIENT IS WITHHELD FROM ALMOST EVERYBODY. Reach is:
 *    you read every incident, or you filed it, or it names a client in your
 *    caseload. A staff-only, program-only or location-only incident matches none
 *    of those, so it is invisible unless you filed it or hold
 *    `incidents:read_all` AND `directory:read_all` together. A supervisor's
 *    empty incident list is not an organization with no incidents.
 * 3. `incidents:report` GRANTS NO READ. The write path performs no reach check
 *    at all, so a worker can file an incident and immediately be unable to open
 *    it — except that filing it makes them `reported_by`, which IS a reach.
 *    A screen must not assume the reverse.
 * 4. THREE DIFFERENT 409s SHARE ONE SHAPE. `IncidentStateError` carries no
 *    `reason`, so "already closed", "already has a follow-up" and "a closed
 *    incident cannot be edited" are distinguishable only by their message. Show
 *    the server's sentence rather than branching on a code that is not there.
 *    The two 409s that DO carry a `reason` are the assignment refusals.
 *
 * EVERY ROUTE ALSO NEEDS `directory:read`, reads and writes alike, because an
 * incident names the person who reported it and the worker involved.
 */
import { get, patch, post } from './transport.js';
// ----------------------------------------------------------------- constants
/** The route gates, re-exported so a screen and a route cannot drift apart. */
export const INCIDENTS_READ = 'incidents:read';
export const INCIDENTS_REPORT = 'incidents:report';
export const INCIDENTS_MANAGE = 'incidents:manage';
/**
 * Widens reach to every incident in the organization — but ONLY in conjunction
 * with `directory:read_all`. The server requires both, and neither alone is a
 * route gate.
 */
export const INCIDENTS_READ_ALL = 'incidents:read_all';
/** The second gate on every route in this domain. */
export const INCIDENTS_DIRECTORY_READ = 'directory:read';
/**
 * `GET /api/incidents` — needs `incidents:read` + `directory:read`.
 *
 * THERE IS NO TOTAL AND NO SCOPE FIELD ON THIS RESPONSE, which is the important
 * absence. The rows are narrowed in SQL by reach, so an unreachable incident is
 * missing rather than refused, and the client is given nothing that says how far
 * the narrowing went. A count taken from this array is a claim about the
 * caller's reach; render it as one, and never as "the organization had N
 * incidents". See rules 1 and 2 in the module header.
 */
export async function list(query = {}, options) {
    const body = await get('/api/incidents', {
        ...options,
        query: { ...query },
    });
    return body.incidents;
}
/**
 * `POST /api/incidents` → 201. Needs `incidents:report`.
 *
 * No reach check on the write, deliberately: a worker reporting an incident
 * about a client they cannot otherwise read must not be stopped, and must not
 * learn from the refusal whether that client exists.
 */
export async function report(input, options) {
    const body = await post('/api/incidents', input, options);
    return body.incident;
}
/**
 * `GET /api/incidents/:id` — returned BARE, not in an envelope.
 *
 * 404 means withheld OR absent and the server will not say which.
 */
export function detail(id, options) {
    return get(`/api/incidents/${id}`, options);
}
/**
 * `PATCH /api/incidents/:id` — needs `incidents:manage`.
 *
 * A CLOSED INCIDENT CANNOT BE EDITED (409). Reopening is not a route this
 * domain offers, so closing is effectively terminal from the client's side.
 */
export async function manage(id, input, options) {
    const body = await patch(`/api/incidents/${id}`, input, options);
    return body.incident;
}
/**
 * `POST /api/incidents/:id/follow-up` → 201. Needs `incidents:manage`.
 *
 * ONE FOLLOW-UP PER INCIDENT — a second attempt is a 409. The task lives in the
 * canonical `task` queue; this domain does not open a second one.
 */
export async function raiseFollowUp(id, input, options) {
    const body = await post(`/api/incidents/${id}/follow-up`, input, options);
    return body.incident;
}
/** `POST /api/incidents/:id/resolution` → 200. Needs `incidents:manage`. */
export async function resolve(id, input, options) {
    const body = await post(`/api/incidents/${id}/resolution`, input, options);
    return body.incident;
}
/**
 * The `reason` on a 409, when there is one.
 *
 * Only the two assignment refusals carry it. A plain `IncidentStateError` — the
 * three most common conflicts — carries no `reason` at all, so this answering
 * null does NOT mean the refusal was un-coded by accident: it means the server
 * has no code for that family and the message is the whole of what it said.
 */
export function assignmentRefusal(err) {
    if (typeof err !== 'object' || err === null)
        return null;
    if (err.status !== 409)
        return null;
    const body = err.body;
    if (typeof body !== 'object' || body === null)
        return null;
    const reason = body.reason;
    return reason === 'assignee_lacks_participant_authority' ||
        reason === 'incident_participant_absent'
        ? reason
        : null;
}
/**
 * Which of the four things an incident can be ABOUT this row names.
 *
 * `incident_related_ck` guarantees at least one, so an empty result is
 * impossible from the server and would mean the row was assembled elsewhere.
 * Exported because "who is this incident about" is the question every list cell
 * asks, and deriving it inline in three places is how the four get to disagree.
 */
export function participantsOf(incident) {
    const kinds = [];
    if (incident.clientId !== null)
        kinds.push('client');
    if (incident.worker !== null)
        kinds.push('staff');
    if (incident.programId !== null)
        kinds.push('program');
    if (incident.locationId !== null)
        kinds.push('location');
    return kinds;
}
//# sourceMappingURL=incidents.js.map